import { InfoCircleFilled } from '@ant-design/icons'
import Flex from 'antd/lib/flex'
import Form from 'antd/lib/form'
import { FormInstance } from 'antd/lib/form/hooks/useForm'
import Row from 'antd/lib/grid/row'
import Input from 'antd/lib/input'
import TextArea from 'antd/lib/input/TextArea'
import Radio from 'antd/lib/radio'
import Tooltip from 'antd/lib/tooltip'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import MarkdownRenderer from 'Components/common/MarkdownRenderer/MarkdownRenderer'
import { useEffect, useMemo, useCallback } from 'react'
import classes from './TurnStepResponse.module.less'
import { TurnResponseFormType } from 'Types/Tasks.types'

type TurnStepIndividualResponse = {
    firstIncorrectStep: number | null
    step: {
        content: string
        number: number
    }
    index: number
    isCorrectResponse: boolean
    hasManualSteps: boolean
    isViewMode: boolean
    form: FormInstance<TurnResponseFormType>
}

type TurnStepResponsePropsType = {
    firstIncorrectStep: number | null
    setFirstIncorrectStep: (val: number | null) => void
    steps: {
        content: string
        number: number
    }[]
    isCorrectResponse: boolean
    submitTurnData: () => void
    humanResponseNeeded: boolean
    turn: number
    retryCount: number
    hasManualSteps: boolean
    form: FormInstance<TurnResponseFormType>
    isReviewMode?: boolean
    initialValues?: TurnResponseFormType
    isReadOnly?: boolean
    setTurnStepResponseForm: (form: FormInstance<TurnResponseFormType>) => void
}

const TurnStepIndividualResponse = (props: TurnStepIndividualResponse) => {
    const { firstIncorrectStep, step, index, isCorrectResponse, hasManualSteps, isViewMode, form } =
        props
    const stepContentWithErrors = useMemo(() => {
        if (
            firstIncorrectStep !== null &&
            (isViewMode ? step.number : index) === firstIncorrectStep
        ) {
            return (
                <>
                    <Row>
                        <Form.Item<TurnResponseFormType>
                            name={['values', index, 'error_identification']}
                            label={`Why do you think this step is incorrect?`}
                            className={classes.errorFormItem}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter why the step is incorrect.',
                                },
                            ]}
                        >
                            <TextArea rows={6} />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item<TurnResponseFormType>
                            name={['values', index, 'error_correction']}
                            label={`Mention correct content of this step.`}
                            className={classes.errorFormItem}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter the correct content of the step',
                                },
                            ]}
                        >
                            <TextArea rows={6} disabled={isViewMode || hasManualSteps} />
                        </Form.Item>
                        {isViewMode && (
                            <div className={classes.previewContainer}>
                                <MarkdownRenderer
                                    escapeBackslashses={true}
                                    children={form.getFieldValue([
                                        'values',
                                        index,
                                        'error_correction',
                                    ])}
                                />
                            </div>
                        )}
                    </Row>
                </>
            )
        } else {
            return (
                <Row>
                    <div className={classes.previewContainer}>
                        <MarkdownRenderer children={step.content} escapeBackslashses={true} />
                    </div>
                </Row>
            )
        }
    }, [firstIncorrectStep, form, index, isViewMode, step.content, step.number, hasManualSteps])

    if (firstIncorrectStep === null || index <= firstIncorrectStep) {
        return (
            <div className={classes.stepContainer}>
                <Form.Item<TurnResponseFormType>
                    name={['values', index, 'accuracy']}
                    label={`Is step correct?`}
                    className={classes.formItem}
                >
                    <Radio.Group disabled={isCorrectResponse || hasManualSteps || isViewMode}>
                        <Radio value="YES">Yes</Radio>
                        <Radio value="NO">No</Radio>
                    </Radio.Group>
                </Form.Item>
                <Form.Item<TurnResponseFormType>
                    name={['values', index, 'python_or_text']}
                    label={
                        <Flex vertical>
                            <Flex gap={4}>
                                <div>Is this step LLM based vs Python based?</div>
                                <Tooltip
                                    placement="right"
                                    title={
                                        'If the step has mathematical calculations or reasoning that a human can conduct without a calculator, such as (e.g., "if a=b and b>c, then a>c" or "2*5" ) it would be considered as an "LLM" step.If the step has calculations that requires a calculator such as (1.063^3), it would be considered as a "Python" step.We should keep the amount requiring "Python" limited.'
                                    }
                                >
                                    <InfoCircleFilled />
                                </Tooltip>
                            </Flex>
                            <div className={classes.subLabel}>
                                Steps involving reasoning or simple math (ex. "if a`
                                {'>'}
                                `b`{'>'}`c, then a`{'>'}`c" or "2*5") are "LLM" based. More complex
                                calculations needing a calculator (ex. 1.063^3) are "Python" based.
                            </div>
                        </Flex>
                    }
                    className={classes.formItem}
                >
                    <Radio.Group>
                        <Radio value="PYTHON">Python</Radio>
                        <Radio value="LLM">LLM</Radio>
                    </Radio.Group>
                </Form.Item>
                <Row>
                    <div className={classes.contentContainer}>
                        <p className={classes.contentTitle}>Step content</p>
                        <div className={classes.content}>{step.content}</div>
                    </div>
                </Row>

                {stepContentWithErrors}
            </div>
        )
    } else {
        return null
    }
}

export const TurnStepResponse = (props: TurnStepResponsePropsType) => {
    const {
        firstIncorrectStep,
        setFirstIncorrectStep,
        steps,
        isCorrectResponse,
        submitTurnData,
        humanResponseNeeded,
        turn,
        retryCount,
        hasManualSteps,
        form,
        isReviewMode,
        isReadOnly,
        setTurnStepResponseForm,
    } = props

    useEffect(() => {
        setTurnStepResponseForm(form)
    }, [form, setTurnStepResponseForm])

    useEffect(() => {
        if (!props.initialValues) {
            const updatedValues = {
                human_final_answer: '',
                values: steps.map(step => ({
                    step_number: step.number,
                    accuracy: 'YES',
                    python_or_text: 'PYTHON',
                    error_identification: null,
                    error_correction: null,
                })),
            }
            props.form.setFieldsValue(updatedValues)
        }
    }, [steps, props.form, props.initialValues])

    const onFormValuesChange = (values: TurnResponseFormType['values']) => {
        if (isCorrectResponse) {
            return
        }

        const firstIncorrectStep = values.findIndex(value => value.accuracy === 'NO')
        setFirstIncorrectStep(firstIncorrectStep === -1 ? null : firstIncorrectStep)

        for (let i = firstIncorrectStep + 1; i < values.length; i++) {
            form.setFieldValue(['values', i, 'accuracy'], 'YES')
            form.setFieldValue(['values', i, 'error_identification'], null)
            form.setFieldValue(['values', i, 'error_correction'], null)
        }
    }

    const correctResponseContainer = useMemo(() => {
        if (isCorrectResponse && !isReviewMode) {
            return (
                <Form.Item<TurnResponseFormType>
                    name={'human_final_answer'}
                    label={<p className={classes.inputTitle}>Final Short Answer</p>}
                    rules={[{ required: true, message: 'Final short answer is required' }]}
                >
                    <Input className={classes.input} placeholder="Eg: (5, x=4x)" />
                </Form.Item>
            )
        }

        return null
    }, [isCorrectResponse, isReviewMode])

    const actionButton = useMemo(() => {
        if ((!humanResponseNeeded || isCorrectResponse) && !isReviewMode) {
            return (
                <ActionButton
                    onClick={() => form.submit()}
                    title={isCorrectResponse ? 'Finish & Submit' : 'Regenerate'}
                    disabled={
                        (firstIncorrectStep === null && !isCorrectResponse) ||
                        (turn === 1 && isCorrectResponse && retryCount < 3)
                    }
                    className={classes.submitButton}
                />
            )
        }

        return null
    }, [
        firstIncorrectStep,
        form,
        humanResponseNeeded,
        isCorrectResponse,
        isReviewMode,
        retryCount,
        turn,
    ])

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter' && event.shiftKey === false) {
            event.preventDefault()
        }
    }, [])

    return (
        <div className={classes.section}>
            <p className={classes.title}>Section 2: IDENTIFY CORRECT AND WRONG STEPS</p>

            <Form
                initialValues={props.initialValues}
                form={form}
                layout="vertical"
                onValuesChange={(_, values) => onFormValuesChange(values.values)}
                disabled={hasManualSteps || isReadOnly}
                onFinish={submitTurnData}
                onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                    handleKeyDown(e)
                }}
            >
                {steps.map((step, index) => (
                    <TurnStepIndividualResponse
                        firstIncorrectStep={firstIncorrectStep}
                        step={step}
                        index={index}
                        isCorrectResponse={isCorrectResponse}
                        hasManualSteps={hasManualSteps}
                        key={step.number}
                        isViewMode={isReviewMode ?? false}
                        form={form}
                    />
                ))}

                <div className={classes.container}>
                    {correctResponseContainer}
                    {actionButton}
                </div>
            </Form>
        </div>
    )
}
